import { getSocketApiEncryptionKey } from '../variables';

const LARGE_SIGNALING_FIELDS = ['sdp', 'candidate', 'answer'];
const ENCRYPTED_METADATA_FIELDS = ['admin_id', 'aluno_id', 'faculdade_id', 'processo_id', 'processo', 'student_id', 'sender_id', 'receive_id', 'sala_id', 'external'];

export default function secureSocket(socket) {
    if (!socket || socket.__secureSocketWrapped) {
        return socket;
    }

    const originalOn = socket.on.bind(socket);
    const originalEmit = socket.emit.bind(socket);

    socket.on = function (eventName, handler) {
        if (typeof handler !== 'function') {
            return originalOn(eventName, handler);
        }

        return originalOn(eventName, function () {
            const context = this;
            const args = Array.prototype.slice.call(arguments);
            const runHandler = (handlerArgs) => {
                const result = handler.apply(context, handlerArgs);
                if (result && typeof result.catch === 'function') {
                    result.catch((e) => logSocketError('Socket event catch', eventName, handlerArgs, e));
                }
                return result;
            };

            try {
                if (getEncryptionKey() && canEncrypt() && args.some(hasEncryptedPayload)) {
                    decryptArgs(args).then(runHandler).catch((e) => {
                        logSocketError('Socket event catch', eventName, args, e);
                        runHandler(args);
                    });
                    return;
                }

                return runHandler(args);
            }
            catch (e) {
                logSocketError('Socket event catch', eventName, args, e);
            }
        });
    };

    socket.emit = function (eventName) {
        try {
            if (getEncryptionKey() && canEncrypt()) {
                const args = Array.prototype.slice.call(arguments, 1);
                encryptArgs(args).then((encryptedArgs) => {
                    originalEmit.apply(socket, [eventName].concat(encryptedArgs));
                }).catch((e) => {
                    logSocketError('Socket encrypt catch', eventName, args, e);
                    originalEmit.apply(socket, arguments);
                });
                return socket;
            }

            return originalEmit.apply(socket, arguments);
        }
        catch (e) {
            logSocketError('Socket emit catch', eventName, Array.prototype.slice.call(arguments, 1), e);
        }
    };

    socket.__secureSocketWrapped = true;
    return socket;
}

function getEncryptionKey() {
    return getSocketApiEncryptionKey();
}

function canEncrypt() {
    return typeof window !== 'undefined'
        && window.crypto
        && window.crypto.subtle
        && typeof TextEncoder !== 'undefined'
        && typeof TextDecoder !== 'undefined';
}

function logSocketError(message, eventName, data, error) {
    try {
        console.log(message, {
            event: eventName,
            data: data,
            error: error && error.stack ? error.stack : error
        });
    }
    catch (ignored) {
    }
}

function toBase64(bytes) {
    let binary = '';
    bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
    });
    return window.btoa(binary);
}

function base64ToBytes(value) {
    const binary = window.atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}

async function buildCryptoKey() {
    const keyBytes = new TextEncoder().encode(getEncryptionKey());
    const digest = await window.crypto.subtle.digest('SHA-256', keyBytes);
    return window.crypto.subtle.importKey('raw', digest, { name: 'AES-CBC' }, false, ['encrypt', 'decrypt']);
}

async function encryptValue(value) {
    if (typeof value === 'function') {
        return value;
    }

    if (hasLargeSignalingPayload(value)) {
        return encryptSignalingMetadata(value);
    }

    const iv = window.crypto.getRandomValues(new Uint8Array(16));
    const key = await buildCryptoKey();
    const encoded = new TextEncoder().encode(JSON.stringify(value));
    const encrypted = await window.crypto.subtle.encrypt({ name: 'AES-CBC', iv: iv }, key, encoded);

    return {
        __encrypted: true,
        payload: toBase64(new Uint8Array(encrypted)),
        iv: toBase64(iv)
    };
}

function encryptArgs(args) {
    return Promise.all(args.map(encryptValue));
}

function isEncryptedEnvelope(value) {
    return value != null && typeof value === 'object' && value.__encrypted === true && value.payload && value.iv;
}

function hasEncryptedPayload(value) {
    return containsEncryptedEnvelope(value) || containsEncryptedEnvelope(parseJsonString(value));
}

function containsEncryptedEnvelope(value) {
    if (isEncryptedEnvelope(value)) {
        return true;
    }
    if (Array.isArray(value)) {
        return value.some(containsEncryptedEnvelope);
    }
    if (value != null && typeof value === 'object') {
        return Object.keys(value).some((key) => containsEncryptedEnvelope(value[key]));
    }
    return false;
}

function parseJsonString(value) {
    if (typeof value !== 'string') {
        return null;
    }
    try {
        return JSON.parse(value);
    }
    catch (e) {
        return null;
    }
}

function hasLargeSignalingPayload(value) {
    const parsed = typeof value === 'string' ? parseJsonString(value) : value;
    return parsed != null
        && typeof parsed === 'object'
        && LARGE_SIGNALING_FIELDS.some((field) => parsed[field] !== undefined);
}

async function encryptSignalingMetadata(value) {
    const isString = typeof value === 'string';
    const parsed = isString ? parseJsonString(value) : value;
    const encrypted = await encryptMetadataFields(parsed);

    return isString ? JSON.stringify(encrypted) : encrypted;
}

async function encryptMetadataFields(value) {
    if (Array.isArray(value)) {
        return Promise.all(value.map(encryptMetadataFields));
    }
    if (value == null || typeof value !== 'object' || isEncryptedEnvelope(value)) {
        return value;
    }

    const encrypted = {};
    for (const key of Object.keys(value)) {
        encrypted[key] = ENCRYPTED_METADATA_FIELDS.indexOf(key) !== -1 ? await encryptValue(value[key]) : value[key];
    }
    return encrypted;
}

async function decryptValue(value) {
    if (typeof value === 'string') {
        const parsed = parseJsonString(value);
        if (containsEncryptedEnvelope(parsed)) {
            return JSON.stringify(await decryptValue(parsed));
        }
    }

    if (Array.isArray(value)) {
        return Promise.all(value.map(decryptValue));
    }

    if (value != null && typeof value === 'object' && !isEncryptedEnvelope(value)) {
        const decryptedObject = {};
        for (const key of Object.keys(value)) {
            decryptedObject[key] = await decryptValue(value[key]);
        }
        return decryptedObject;
    }

    if (!isEncryptedEnvelope(value)) {
        return value;
    }

    const key = await buildCryptoKey();
    const decrypted = await window.crypto.subtle.decrypt({ name: 'AES-CBC', iv: base64ToBytes(value.iv) }, key, base64ToBytes(value.payload));
    return JSON.parse(new TextDecoder().decode(decrypted));
}

function decryptArgs(args) {
    return Promise.all(args.map(decryptValue));
}
